/**
 * Bill of Materials (BOM) Calculator
 *
 * Recursive algorithm for calculating complete material requirements
 */

import { parseSkills, parseIndustrySkill } from '@/utils';

/**
 * Base Material Efficiency in Eve Echoes
 * Players start at 150% ME (requiring 1.5x blueprint materials)
 * Skills reduce this towards 100% (perfect efficiency)
 */
const BASE_ME = 1.5;
import type {
  ItemBlueprint,
  IndustrySkill,
  ParsedMaterial,
  BomResult,
  BomOptions,
  BomContext,
  MaterialMap,
  IntermediateMap,
  RawMaterial,
  IntermediateItem,
} from '@/types';

/**
 * BOM Calculator Service
 */
export class BomCalculator {
  private blueprintMap: Map<string, ItemBlueprint> = new Map();
  private skillMap: Map<string, IndustrySkill> = new Map();

  /**
   * Initialize calculator with blueprint and skill data
   */
  constructor(blueprints: ItemBlueprint[], skills: IndustrySkill[]) {
    // Create blueprint lookup by item_id (store as string for consistency)
    blueprints.forEach((bp) => {
      this.blueprintMap.set(String(bp.item_id), bp);
    });

    // Create skill lookup by name
    skills.forEach((skill) => {
      this.skillMap.set(skill.name, skill);
    });
  }

  /**
   * Calculate complete BOM for a blueprint
   */
  calculate(blueprint: ItemBlueprint, options: BomOptions = {}): BomResult {
    const startTime = Date.now();

    // Validate inputs
    const targetQuantity = options.targetQuantity ?? 1;
    if (targetQuantity <= 0) {
      throw new Error('Target quantity must be greater than 0');
    }

    // Initialize context
    // Structure bonus is a percentage (e.g., 6 = 6%) that subtracts from base ME
    const structureMEBonusPercent = options.structureBonuses?.manufacturingME ?? 0;
    const context: BomContext = {
      blueprints: this.blueprintMap,
      userSkills: options.userSkills ?? {},
      structureBonusPercent: structureMEBonusPercent, // Store as percentage for additive calc
      materialMap: {},
      intermediateMap: {},
      visitedItems: new Set(),
      skillsApplied: new Set(),
      skillDetails: new Map(),
      applyEfficiency: options.applyEfficiency ?? true,
    };

    // Calculate runs needed (blueprint may output multiple items per run)
    const runs = Math.ceil(targetQuantity / blueprint.output_number);
    const actualQuantity = runs * blueprint.output_number;

    // Start recursive calculation
    this.calculateRecursive(String(blueprint.item_id), runs, context);

    // Convert maps to arrays
    const rawMaterials = this.materialMapToArray(context.materialMap);
    const intermediateItems =
      options.includeIntermediates !== false
        ? this.intermediateMapToArray(context.intermediateMap)
        : [];

    // Calculate total cost (sum of adjusted quantities)
    const totalRawMaterialCost = rawMaterials.reduce((sum, mat) => sum + mat.adjustedQuantity, 0);

    // Calculate final Material Efficiency for display
    let finalMaterialEfficiency: number | undefined;
    if (context.applyEfficiency) {
      const totalSkillBonus = Array.from(context.skillDetails.values()).reduce(
        (sum, skill) => sum + skill.efficiencyPercent,
        0
      );
      // Final ME = Base ME (150%) - Total Skill Bonus - Structure Bonus
      // All bonuses are ADDITIVE (subtract from base 150%)
      const finalME = 150 - totalSkillBonus - context.structureBonusPercent;
      finalMaterialEfficiency = finalME;
    }

    return {
      targetItem: blueprint.name,
      targetQuantity: actualQuantity,
      rawMaterials,
      intermediateItems,
      totalRawMaterialCost,
      calculationTime: Date.now() - startTime,
      skillsApplied: Array.from(context.skillsApplied),
      skillDetails: Array.from(context.skillDetails.values()),
      finalMaterialEfficiency,
    };
  }

  /**
   * Recursive BOM calculation
   */
  private calculateRecursive(itemId: string, quantity: number, context: BomContext): void {
    // Check for circular dependency
    if (context.visitedItems.has(itemId)) {
      const blueprint = context.blueprints.get(itemId);
      throw new Error(`Circular dependency detected: ${blueprint?.name ?? itemId}`);
    }

    // Get blueprint for this item
    const blueprint = context.blueprints.get(itemId);
    if (!blueprint) {
      // This is a raw material (no blueprint exists)
      // This shouldn't happen in the main flow, but handle it gracefully
      return;
    }

    // Mark as visiting (for circular dependency detection)
    context.visitedItems.add(itemId);

    // Convert materials from API format to internal format
    const materials = blueprint.materials.map((m) => ({
      itemId: String(m.id),
      itemName: m.name,
      category: m.type,
      quantity: m.quantity,
    }));

    // Parse skills
    const skillNames = parseSkills(blueprint.skills);

    // Calculate Material Efficiency from user's skills
    const materialEfficiency = this.calculateEfficiency(skillNames, context);

    // Track which skills were applied (if ME is below base 150%)
    if (materialEfficiency < BASE_ME) {
      skillNames.forEach((skill) => context.skillsApplied.add(skill));
    }

    // Process each material
    materials.forEach((material) => {
      // Calculate quantity needed for this many runs
      const baseQuantity = material.quantity * quantity;

      // Check if this material has a blueprint (intermediate item)
      const materialBlueprint = context.blueprints.get(material.itemId);

      if (materialBlueprint) {
        // This is an intermediate manufactured item
        this.handleIntermediateItem(
          material,
          baseQuantity,
          materialEfficiency,
          materialBlueprint,
          context
        );
      } else {
        // This is a raw material
        this.addRawMaterial(material, baseQuantity, materialEfficiency, context);
      }
    });

    // Done visiting this item
    context.visitedItems.delete(itemId);
  }

  /**
   * Handle an intermediate manufactured item
   */
  private handleIntermediateItem(
    _material: ParsedMaterial,
    baseQuantity: number,
    materialEfficiency: number,
    blueprint: ItemBlueprint,
    context: BomContext
  ): void {
    // Apply ME multiplier
    // materialEfficiency already includes skills (e.g., 1.10 for 110% ME from skills)
    // Structure bonus is ADDITIVE: we need to subtract it from the base 150%
    // If skills give us 110% (reduced by 40%), and structure gives 6%, final is 104%
    // Formula: Final ME = (150% - skills%) - structure% = materialEfficiency - (structure% / 100)
    let finalME = materialEfficiency;
    if (context.applyEfficiency && context.structureBonusPercent > 0) {
      // Convert materialEfficiency percentage back, subtract structure bonus, convert to multiplier
      const skillMEPercent = materialEfficiency * 100; // e.g., 110
      const finalMEPercent = skillMEPercent - context.structureBonusPercent; // e.g., 110 - 6 = 104
      finalME = finalMEPercent / 100; // e.g., 1.04
    }

    const adjustedQuantity = context.applyEfficiency
      ? Math.ceil(baseQuantity * finalME)
      : Math.ceil(baseQuantity);

    // Calculate runs needed
    const runs = Math.ceil(adjustedQuantity / blueprint.output_number);

    // Track this intermediate item
    const itemId = String(blueprint.item_id);
    if (context.intermediateMap[itemId]) {
      // Already tracked, add to quantity
      context.intermediateMap[itemId].quantity += adjustedQuantity;
      context.intermediateMap[itemId].runs += runs;
    } else {
      // New intermediate item
      // Convert materials from API format to internal format
      const materials = blueprint.materials.map((m) => ({
        itemId: String(m.id),
        itemName: m.name,
        category: m.type,
        quantity: m.quantity,
      }));

      context.intermediateMap[itemId] = {
        itemId: blueprint.item_id,
        itemName: blueprint.name,
        blueprint,
        quantity: adjustedQuantity,
        runs,
        materials,
      };
    }

    // Recursively calculate materials for this intermediate item
    this.calculateRecursive(String(blueprint.item_id), runs, context);
  }

  /**
   * Add a raw material to the aggregated map
   */
  private addRawMaterial(
    material: ParsedMaterial,
    baseQuantity: number,
    materialEfficiency: number,
    context: BomContext
  ): void {
    // Apply ME multiplier (same logic as handleIntermediateItem)
    let finalME = materialEfficiency;
    if (context.applyEfficiency && context.structureBonusPercent > 0) {
      const skillMEPercent = materialEfficiency * 100;
      const finalMEPercent = skillMEPercent - context.structureBonusPercent;
      finalME = finalMEPercent / 100;
    }

    const adjustedQuantity = context.applyEfficiency
      ? Math.ceil(baseQuantity * finalME)
      : Math.ceil(baseQuantity);

    const existingMaterial = context.materialMap[material.itemId];
    if (existingMaterial) {
      // Material already exists, add to quantities
      existingMaterial.baseQuantity += baseQuantity;
      existingMaterial.adjustedQuantity += adjustedQuantity;
    } else {
      // New material
      context.materialMap[material.itemId] = {
        itemName: material.itemName,
        category: material.category,
        baseQuantity,
        adjustedQuantity,
      };
    }
  }

  /**
   * Calculate Material Efficiency (ME) from user skills
   *
   * Eve Echoes starts at 150% ME (requiring 1.5x blueprint materials)
   * Skills reduce this towards 100% ME (perfect efficiency)
   * Multiple skills stack additively
   *
   * @returns Final ME multiplier (e.g., 0.96 for 96% ME)
   */
  private calculateEfficiency(skillNames: string[], context: BomContext): number {
    if (!context.applyEfficiency || skillNames.length === 0) {
      return BASE_ME; // No skills = 150% ME
    }

    let totalSkillBonus = 0;

    skillNames.forEach((skillName) => {
      const userLevel = context.userSkills[skillName];

      if (!userLevel || userLevel < 1 || userLevel > 5) {
        return; // Skill not trained or invalid level
      }

      const skill = this.skillMap.get(skillName);
      if (!skill) {
        return; // Skill data not found
      }

      const parsed = parseIndustrySkill(skill);
      const efficiency = parsed.efficiencyByLevel[userLevel - 1];
      if (efficiency !== undefined) {
        // Skills are additive: sum all applicable skills
        totalSkillBonus += efficiency / 100;

        // Track skill details for tooltip display
        context.skillDetails.set(skillName, {
          name: skillName,
          level: userLevel,
          efficiencyPercent: efficiency,
        });
      }
    });

    // Final ME = Base ME (150%) - Total Skill Bonus
    // This can go below 100% with high skills + citadel bonuses (future)
    const finalME = BASE_ME - totalSkillBonus;

    return finalME;
  }

  /**
   * Convert material map to sorted array
   */
  private materialMapToArray(materialMap: MaterialMap): RawMaterial[] {
    return Object.entries(materialMap)
      .map(([itemId, data]) => ({
        itemId,
        itemName: data.itemName,
        category: data.category,
        quantity: data.baseQuantity,
        adjustedQuantity: data.adjustedQuantity,
      }))
      .sort((a, b) => {
        // Sort by category first, then by name
        if (a.category !== b.category) {
          return a.category.localeCompare(b.category);
        }
        return a.itemName.localeCompare(b.itemName);
      });
  }

  /**
   * Convert intermediate map to sorted array
   */
  private intermediateMapToArray(intermediateMap: IntermediateMap): IntermediateItem[] {
    return Object.values(intermediateMap).sort((a, b) => {
      return a.itemName.localeCompare(b.itemName);
    });
  }
}
